import React, { useCallback, useEffect, useRef, useState } from 'react';

export type WidthAnchor = 'left' | 'center' | 'right';

interface TextWidthHandlesProps {
  /** Current width as percentage (0-100) of available container width. */
  widthPct: number;
  /** Current offset from the anchor edge as percentage (0-100). */
  widthOffsetPct: number;
  /** Horizontal anchor of the text element — derived from position string. */
  anchor: WidthAnchor;
  onChange: (next: { width: number; widthOffset: number }) => void;
  min?: number;
  max?: number;
  visible: boolean;
  onDraggingChange?: (dragging: boolean) => void;
}

/**
 * Drag affordances on the left/right edges of a text element. Each handle
 * moves the EDGE it sits on, regardless of which side is anchored:
 *
 * - LEFT anchor + LEFT handle: shifts the box right (width offset grows, width shrinks)
 * - LEFT anchor + RIGHT handle: extends/shrinks the right edge (width changes)
 * - RIGHT anchor + RIGHT handle: shifts the box left (width offset grows, width shrinks)
 * - RIGHT anchor + LEFT handle: extends/shrinks the left edge (width changes)
 * - CENTER anchor: width changes symmetrically (offset stays 0)
 */
const TextWidthHandles: React.FC<TextWidthHandlesProps> = ({
  widthPct,
  widthOffsetPct,
  anchor,
  onChange,
  min = 20,
  max = 100,
  visible,
  onDraggingChange,
}) => {
  const [dragging, setDragging] = useState<null | 'left' | 'right'>(null);
  const startRef = useRef<{ x: number; width: number; widthOffset: number; containerW: number } | null>(null);

  const onPointerDown = (side: 'left' | 'right') => (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const handle = e.currentTarget as HTMLDivElement;
    // Width reference is the parent's parent (the overlay padded area row).
    const reference = handle.parentElement?.parentElement;
    const containerW = reference ? reference.getBoundingClientRect().width : 1;
    startRef.current = { x: e.clientX, width: widthPct, widthOffset: widthOffsetPct, containerW };
    setDragging(side);
    onDraggingChange?.(true);
    handle.setPointerCapture(e.pointerId);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: PointerEvent) => {
      const start = startRef.current;
      if (!start) return;
      const dxPct = ((e.clientX - start.x) / start.containerW) * 100;

      let nextWidth = start.width;
      let nextOffset = start.widthOffset;

      if (anchor === 'left') {
        if (dragging === 'left') {
          // Shift left edge: the offset grows, width shrinks (right edge stays).
          nextOffset = start.widthOffset + dxPct;
          nextWidth = start.width - dxPct;
        } else {
          // Right handle: width follows cursor.
          nextWidth = start.width + dxPct;
        }
      } else if (anchor === 'right') {
        if (dragging === 'right') {
          // Shift right edge: offset grows, width shrinks (left edge stays).
          nextOffset = start.widthOffset - dxPct;
          nextWidth = start.width + dxPct;
        } else {
          // Left handle: left edge follows cursor.
          nextWidth = start.width - dxPct;
        }
      } else {
        // Center: keep offset at 0, just resize symmetrically by width.
        if (dragging === 'left') {
          nextWidth = start.width - dxPct;
        } else {
          nextWidth = start.width + dxPct;
        }
        nextOffset = 0;
      }

      // Clamp width first, then offset so it stays in [0, 100 - width].
      nextWidth = Math.min(max, Math.max(min, nextWidth));
      const offsetMax = Math.max(0, 100 - nextWidth);
      nextOffset = Math.min(offsetMax, Math.max(0, nextOffset));

      onChange({ width: nextWidth, widthOffset: nextOffset });
    };
    const onUp = () => {
      setDragging(null);
      onDraggingChange?.(false);
      startRef.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [dragging, anchor, onChange, onDraggingChange, min, max]);

  // Hit area is always interactive so the user can grab the edge directly even
  // before the wrapper's hover handler has fired. Visual indicator only shows
  // when the parent says so.
  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 14,
    cursor: 'ew-resize',
    zIndex: 5,
    pointerEvents: 'auto',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  // 2px-wide visible pill that hints the drag affordance. Always rendered
  // inside the handle but only visible when `visible` or `dragging`.
  const hintStyle: React.CSSProperties = {
    width: 2,
    height: '60%',
    minHeight: 12,
    maxHeight: 28,
    borderRadius: 1,
    background: 'rgba(255,255,255,0.85)',
    boxShadow: '0 0 4px rgba(0,0,0,0.6)',
    opacity: visible || dragging ? 1 : 0,
    transition: 'opacity 120ms ease',
    pointerEvents: 'none',
  };

  return (
    <>
      <div
        style={{ ...baseStyle, left: -9 }}
        onPointerDown={onPointerDown('left')}
        title="Drag to resize width"
      >
        <div style={hintStyle} />
      </div>
      <div
        style={{ ...baseStyle, right: -9 }}
        onPointerDown={onPointerDown('right')}
        title="Drag to resize width"
      >
        <div style={hintStyle} />
      </div>
    </>
  );
};

export default TextWidthHandles;
